import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useLocation, useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Save, DollarSign, CreditCard, Smartphone, FileText, Loader2, AlertTriangle, Tag, Clock, Phone } from 'lucide-react';
import { API_URL } from '../../api/config';

const HoldBills = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { selectedItems = [], tableId } = location.state || {};
  const { userToken: token, restaurantId } = useAuth();
  
  const [names, setNames] = useState('');
  const [mobileNumber, setMobileNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [holdBills, setHoldBills] = useState([]);
  const [error, setError] = useState(null);
  const [discountPercentage, setDiscountPercentage] = useState(0);
  const [discountAmount] = useState(0);
  const [paymentMode, setPaymentMode] = useState('Cash');

  const calculateTotals = (items) => {
    const CGST_RATE = 0.025;
    const SGST_RATE = 0.025;
    
    let taxableAmount = 0;
    let nonTaxableAmount = 0;
    
    items.forEach(item => {
      let itemPrice;
      if (item.selectedVariant && item.selectedVariant.price !== undefined) {
        itemPrice = item.selectedVariant.price;
      } else if (item.price !== undefined) {
        itemPrice = item.price;
      } else if (item.pricingType === 'mrp' && item.mrp !== undefined) {
        itemPrice = item.mrp;
      } else if (item.basePrice !== undefined) {
        itemPrice = item.basePrice;
      } else {
        console.warn('Could not determine price for item:', item);
        itemPrice = 0;
      }
      
      const itemTotal = itemPrice * (item.quantity || 1);
      
      if (item.pricingType === 'basePrice') {
        taxableAmount += itemTotal;
      } else {
        nonTaxableAmount += itemTotal;
      }
    });
    
    const subtotal = taxableAmount + nonTaxableAmount;
    const cgst = taxableAmount * CGST_RATE;
    const sgst = taxableAmount * SGST_RATE;
    const totalBeforeDiscount = subtotal + cgst + sgst;
    
    const discount =
      discountPercentage > 0
        ? (totalBeforeDiscount * discountPercentage) / 100
        : 0;
    const total = totalBeforeDiscount - discount;
    
    return {
      subtotal: Number(subtotal.toFixed(2)),
      cgst: Number(cgst.toFixed(2)),
      sgst: Number(sgst.toFixed(2)),
      totalBeforeDiscount: Number(totalBeforeDiscount.toFixed(2)),
      discount: Number(discount.toFixed(2)),
      total: Number(total.toFixed(2))
    };
  };

  const validateItems = (items) => {
    if (!Array.isArray(items) || items.length === 0) {
      throw new Error('No items selected');
    }
    return items.map((item, index) => {
      if (!item?._id) {
        throw new Error(`Missing product ID for item ${index + 1}`);
      }
      if (!item?.itemName) {
        throw new Error(`Missing product name for item ${index + 1}`);
      }
      
      const validatedItem = {
        productId: item._id,
        itemName: item.itemName,
        quantity: item.quantity || 1,
        category: item.category || 'Veg',
        pricingType: item.pricingType,
        price: item.selectedVariant 
          ? item.selectedVariant.price 
          : (item.pricingType === 'mrp' ? item.mrp : item.basePrice)
      };

      if (item.selectedVariant) {
        validatedItem.variant = {
          name: item.selectedVariant.name,
          price: item.selectedVariant.price
        };
      }

      return validatedItem;
    });
  };

  const validateMobileNumber = (number) => {
    if (!number) return true; // Optional field
    const mobileRegex = /^[6-9]\d{9}$/;
    return mobileRegex.test(number);
  };

  const BillItem = ({ item }) => {
    let itemPrice;
    if (item.selectedVariant && item.selectedVariant.price !== undefined) {
      itemPrice = item.selectedVariant.price;
    } else if (item.price !== undefined) {
      itemPrice = item.price;
    } else if (item.pricingType === 'mrp' && item.mrp !== undefined) {
      itemPrice = item.mrp;
    } else if (item.basePrice !== undefined) {
      itemPrice = item.basePrice;
    } else {
      itemPrice = 0;
    }
    
    const quantity = item.quantity || 1;
    const totalPrice = itemPrice * quantity;
    
    return (
      <div className="flex justify-between items-start border-b border-gray-200 py-3">
        <div className="flex-1">
          <p className="font-medium text-gray-800">
            {item.itemName}
            {item.selectedVariant && (
              <span className="text-gray-600 ml-1">- {item.selectedVariant.name}</span>
            )} 
            <span className="text-gray-500 ml-1">(x{quantity})</span>
          </p>
          {item.pricingType === 'basePrice' && (
            <p className="text-xs text-blue-600">+5% GST</p>
          )}
        </div>
        <p className="font-medium text-gray-800">₹{totalPrice.toFixed(2)}</p>
      </div>
    );
  };

  const saveHoldBill = async () => {
    setError(null);
    setLoading(true);
    try {
      if (!tableId) throw new Error('Table ID is required');
      if (!restaurantId) throw new Error('Restaurant authentication required');

      // Validate mobile number if provided
      if (mobileNumber && !validateMobileNumber(mobileNumber)) {
        throw new Error('Please enter a valid 10-digit mobile number');
      }

      const validatedItems = validateItems(selectedItems);
      const { subtotal, cgst, sgst, total, discount } = calculateTotals(selectedItems);

      const payload = {
        tableId,
        restaurantId,
        items: validatedItems,
        subtotal,
        cgst,
        sgst,
        discountPercentage: Number(discountPercentage) || 0,
        discountAmount: discount,
        totalAmount: total,
        names: names.trim(),
        mobileNumber: mobileNumber.trim(),
        status: 'HOLD',
        paymentMode
      };

      const response = await fetch(`${API_URL}/hold-bills`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      
      if (!response.ok) throw new Error(data.message || 'Failed to create hold bill');

      toast.success('Bill placed on hold successfully');
      navigate('/new-bill');
    } catch (error) {
      console.error('Error details:', error);
      setError(error.message);
      toast.error(`Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const PaymentModeSelector = () => {
    const paymentOptions = [
      { mode: 'Cash', icon: <DollarSign size={18} /> },
      { mode: 'UPI', icon: <Smartphone size={18} /> },
      { mode: 'Card', icon: <CreditCard size={18} /> },
      { mode: 'Other', icon: <FileText size={18} /> }
    ];
    
    return (
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">Payment Mode:</label>
        <div className="flex flex-wrap gap-2">
          {paymentOptions.map(({ mode, icon }) => (
            <button
              key={mode}
              className={`flex items-center gap-1 px-4 py-2 rounded-lg border transition-colors ${
                paymentMode === mode 
                  ? 'bg-blue-500 text-white border-blue-500' 
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
              onClick={() => setPaymentMode(mode)}
            >
              {icon}
              <span>{mode}</span>
            </button>
          ))}
        </div>
      </div>
    );
  };

  const renderHoldBillsList = () => {
    return (
      <div className="bg-white rounded-lg shadow-md p-6 max-w-3xl mx-auto">
        <div className="flex items-center gap-2 mb-6">
          <Clock size={24} className="text-blue-600" />
          <h2 className="text-2xl font-bold text-gray-800">Hold Bills</h2>
        </div>
        
        {holdBills.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <FileText size={48} className="mx-auto mb-4 text-gray-400" />
            <p>No hold bills found</p>
          </div>
        ) : (
          <div className="space-y-4">
            {holdBills.map((bill) => (
              <div key={bill._id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                <div className="flex justify-between items-start mb-2">
                  <p className="font-medium">Table: {bill.tableNumber}</p>
                  {bill.mobileNumber && (
                    <div className="flex items-center gap-1 text-sm text-gray-600">
                      <Phone size={14} />
                      <span>{bill.mobileNumber}</span>
                    </div>
                  )}
                </div>
                {bill.names && (
                  <p className="text-sm text-gray-600 mb-2">Names: {bill.names}</p>
                )}
                <div className="grid grid-cols-2 gap-2 mt-2 text-sm">
                  <p>Subtotal: <span className="font-medium">₹{bill.subtotal}</span></p>
                  <p>CGST (2.5%): <span className="font-medium">₹{bill.cgst}</span></p>
                  <p>SGST (2.5%): <span className="font-medium">₹{bill.sgst}</span></p>
                  <p>Discount: <span className="font-medium">₹{bill.discountAmount}</span></p>
                </div>
                <p className="mt-2 text-lg font-semibold text-blue-600">Total: ₹{bill.totalAmount}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      <ToastContainer />

      {selectedItems.length > 0 ? (
        <div className="bg-white rounded-lg shadow-md max-w-5xl mx-auto">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Left: Bill Summary */}
            <div className="p-6 border-b md:border-r md:border-b-0 border-gray-200">
              <div className="flex items-center gap-2 mb-6">
                <FileText size={24} className="text-blue-600" />
                <h2 className="text-2xl font-bold text-gray-800">Hold Bill Details</h2>
              </div>
              
              {error && (
                <div className="flex items-center gap-2 p-3 mb-4 bg-red-50 text-red-700 rounded-md">
                  <AlertTriangle size={18} />
                  <p>{error}</p>
                </div>
              )}
              
              <div className="mb-4 overflow-y-auto max-h-64 border border-gray-100 rounded-lg p-4">
                {selectedItems.map((item, index) => (
                  <BillItem key={index} item={item} />
                ))}
              </div>
              
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <p className="text-gray-600">Subtotal:</p>
                    <p>₹{calculateTotals(selectedItems).subtotal}</p>
                  </div>
                  <div className="flex justify-between">
                    <p className="text-gray-600">CGST (2.5%):</p>
                    <p>₹{calculateTotals(selectedItems).cgst}</p>
                  </div>
                  <div className="flex justify-between">
                    <p className="text-gray-600">SGST (2.5%):</p>
                    <p>₹{calculateTotals(selectedItems).sgst}</p>
                  </div>
                  <div className="flex justify-between">
                    <p className="text-gray-600">Discount:</p>
                    <p>₹{calculateTotals(selectedItems).discount}</p>
                  </div>
                  <div className="flex justify-between pt-2 border-t border-gray-200 text-lg font-bold text-blue-600">
                    <p>Total:</p>
                    <p>₹{calculateTotals(selectedItems).total}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right: Form Inputs */}
            <div className="p-6">
              <div className="mb-4">
                <label htmlFor="names" className="block text-sm font-medium text-gray-700 mb-2">Add names (optional):</label>
                <textarea
                  id="names"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Add names here..."
                  rows="3"
                  value={names}
                  onChange={(e) => setNames(e.target.value)}
                />
              </div>

              <div className="mb-4">
                <label htmlFor="mobileNumber" className="block text-sm font-medium text-gray-700 mb-2">
                  <div className="flex items-center gap-1">
                    <Phone size={16} />
                    <span>Mobile Number (optional):</span>
                  </div>
                </label>
                <input
                  id="mobileNumber"
                  type="tel"
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    mobileNumber && !validateMobileNumber(mobileNumber) 
                      ? 'border-red-300 bg-red-50' 
                      : 'border-gray-300'
                  }`}
                  placeholder="Enter 10-digit mobile number"
                  value={mobileNumber}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, ''); // Remove non-digits
                    if (value.length <= 10) {
                      setMobileNumber(value);
                    }
                  }}
                  maxLength="10"
                />
                {mobileNumber && !validateMobileNumber(mobileNumber) && (
                  <p className="text-red-500 text-xs mt-1">Please enter a valid 10-digit mobile number</p>
                )}
              </div>
              
              <div className="mb-4">
                <label htmlFor="discount" className="block text-sm font-medium text-gray-700 mb-2">
                  <div className="flex items-center gap-1">
                    <Tag size={16} />
                    <span>Discount Percentage (%):</span>
                  </div>
                </label>
                <input
                  id="discount"
                  type="number"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., 10"
                  value={discountPercentage.toString()}
                  onChange={(e) => {
                    const num = Number(e.target.value);
                    if (!isNaN(num) && num >= 0 && num <= 100) {
                      setDiscountPercentage(num);
                    }
                  }}
                  min="0"
                  max="100"
                />
              </div>

              <PaymentModeSelector />

              <button
                className={`mt-4 w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-4 rounded-lg transition-colors ${
                  loading ? 'opacity-75 cursor-not-allowed' : ''
                }`}
                onClick={saveHoldBill}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader2 size={20} className="animate-spin" />
                    <span>Saving...</span>
                  </>
                ) : (
                  <>
                    <Save size={20} />
                    <span>Save Hold Bill</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      ) : (
        renderHoldBillsList()
      )}
    </div>
  );
};

export default HoldBills;
